// Settings — small self-service page: your own passcode and your own Gemini AI keys
// (③ slice 2 / BYOK). Calm, one-thumb. Keys are write-only: after saving, only label + last
// four characters ever show again — there is no way to read a key back out.
// The keys power only the speaking coach, so while it is set aside (COACH_SET_ASIDE, src/nav.ts)
// their section is neither rendered nor wired, and no key is read; the code below stays for its return.

import { api } from "../api";
import { esc } from "../dom";
import { t } from "../i18n";
import { COACH_SET_ASIDE } from "../nav";
import { pageHeader } from "../ui";

type KeyItem = { id: string; label: string | null; last4: string; status: "working" | "resting" | "disabled" };

// Lucide "eye" (stroke 1.75, currentColor) — the design system standardizes on Lucide, not emoji.
const EYE_ICON = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="flex:0 0 auto"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>`;

export function renderSettings(app: HTMLElement): void {
  const keysSection = COACH_SET_ASIDE
    ? ""
    : `
    <h2>${t("settings.keys_title")}</h2>
    <p class="hint">${t("settings.keys_hint")}</p>
    <div id="keylist"><p class="muted">${t("common.loading")}</p></div>
    <form id="addkey" novalidate>
      <label for="keyval">${t("settings.keys_add_label")}</label>
      <input id="keyval" type="password" autocomplete="off" spellcheck="false" placeholder="${t("settings.keys_paste_placeholder")}" required />
      <label for="keylabel">${t("settings.keys_nickname")}</label>
      <input id="keylabel" type="text" maxlength="60" placeholder="${t("settings.keys_nickname_placeholder")}" />
      <div style="height:8px"></div>
      <div id="keyflash" class="toast" hidden>${t("settings.keys_saved")}</div>
      <div id="keyerr" class="error" hidden></div>
      <div style="height:8px"></div>
      <button class="btn" type="submit">${t("settings.keys_add_button")}</button>
    </form>
`;
  app.innerHTML = `
    ${pageHeader({ title: t("settings.title") })}

    <h2>${t("settings.passcode_title")}</h2>
    <p class="hint">${t("settings.passcode_hint")}</p>
    <form id="pass" novalidate>
      <label for="cur">${t("settings.current")}</label>
      <input id="cur" type="password" autocomplete="current-password" required />
      <label for="next">${t("settings.new")}</label>
      <input id="next" type="password" autocomplete="new-password" placeholder="${t("settings.new_placeholder")}" required />
      <div style="height:8px"></div>
      <div id="flash" class="toast" hidden>${t("settings.updated")}</div>
      <div id="err" class="error" hidden></div>
      <div style="height:8px"></div>
      <button class="btn" type="submit">${t("settings.update_button")}</button>
    </form>
${keysSection}
    <div id="coachreads"></div>`;

  const flash = app.querySelector<HTMLElement>("#flash")!;
  const err = app.querySelector<HTMLElement>("#err")!;
  const cur = app.querySelector<HTMLInputElement>("#cur")!;
  const next = app.querySelector<HTMLInputElement>("#next")!;

  app.querySelector<HTMLFormElement>("#pass")!.addEventListener("submit", async (e) => {
    e.preventDefault();
    flash.hidden = true;
    err.hidden = true;
    try {
      await api("/api/me/passcode", {
        method: "POST",
        body: JSON.stringify({ current: cur.value, next: next.value }),
      });
      cur.value = "";
      next.value = "";
      flash.hidden = false;
    } catch (ex) {
      err.textContent = (ex as Error).message;
      err.hidden = false;
    }
  });

  if (!COACH_SET_ASIDE) wireKeys(app);

  // RECORDING_NOTICE's transparency promise: "you can see when your Lead listens." Quiet facts,
  // shown only when there is something to show — oversight is visible, never alarming.
  void (async () => {
    try {
      const { reads } = await api<{ reads: { kind: "read" | "listened"; at: string }[] }>("/api/me/coaching-reads");
      if (reads.length === 0) return;
      const box = app.querySelector<HTMLElement>("#coachreads")!;
      box.innerHTML = `
        <h2>${t("settings.coach_reads_title")}</h2>
        <p class="hint">${t("settings.coach_reads_hint")}</p>
        <div class="coaching-reads">
          ${reads
            .map(
              (r) =>
                `<div class="read-row">${EYE_ICON}<span>${r.kind === "listened" ? t("settings.coach_listened") : t("settings.coach_read")} · ${esc(r.at)}</span></div>`,
            )
            .join("")}
          <div class="remove-line"><button class="linkbtn" type="button">${t("settings.coach_remove")}</button></div>
        </div>`;
    } catch {
      /* the section simply doesn't appear */
    }
  })();
}

// ── AI keys (BYOK) — wired only while the coach is not set aside ─────────────
function wireKeys(app: HTMLElement): void {
  const keylist = app.querySelector<HTMLElement>("#keylist")!;
  const keyflash = app.querySelector<HTMLElement>("#keyflash")!;
  const keyerr = app.querySelector<HTMLElement>("#keyerr")!;
  const keyval = app.querySelector<HTMLInputElement>("#keyval")!;
  const keylabel = app.querySelector<HTMLInputElement>("#keylabel")!;

  const statusLabel = (s: KeyItem["status"]) =>
    s === "working" ? t("settings.keys_status_working") : s === "resting" ? t("settings.keys_status_resting") : t("settings.keys_status_disabled");

  async function loadKeys(): Promise<void> {
    try {
      const data = await api<{ keys: KeyItem[] }>("/api/me/keys");
      if (data.keys.length === 0) {
        keylist.innerHTML = `<p class="muted">${t("settings.keys_none")}</p>`;
        return;
      }
      keylist.innerHTML = data.keys
        .map(
          (k) => `
        <div class="card" style="display:flex;align-items:center;gap:12px;flex-wrap:wrap">
          <div style="flex:1;min-width:120px">
            <div>${k.label ? esc(k.label) : t("settings.keys_unnamed")}</div>
            <div class="muted">····${esc(k.last4)} · ${statusLabel(k.status)}</div>
          </div>
          <button class="btn secondary" data-toggle="${k.id}" data-on="${k.status === "disabled" ? "1" : "0"}" type="button">
            ${k.status === "disabled" ? t("settings.keys_enable") : t("settings.keys_disable")}
          </button>
          <button class="btn secondary" data-del="${k.id}" type="button">${t("common.remove")}</button>
        </div>`,
        )
        .join("");

      keylist.querySelectorAll<HTMLButtonElement>("[data-toggle]").forEach((b) => {
        b.addEventListener("click", async () => {
          await api(`/api/me/keys/${b.dataset.toggle}`, { method: "PATCH", body: JSON.stringify({ enabled: b.dataset.on === "1" }) });
          void loadKeys();
        });
      });
      keylist.querySelectorAll<HTMLButtonElement>("[data-del]").forEach((b) => {
        b.addEventListener("click", async () => {
          await api(`/api/me/keys/${b.dataset.del}`, { method: "DELETE" });
          void loadKeys();
        });
      });
    } catch {
      keylist.innerHTML = `<p class="muted">${t("settings.keys_none")}</p>`;
    }
  }
  void loadKeys();

  app.querySelector<HTMLFormElement>("#addkey")!.addEventListener("submit", async (e) => {
    e.preventDefault();
    keyflash.hidden = true;
    keyerr.hidden = true;
    try {
      await api("/api/me/keys", {
        method: "POST",
        body: JSON.stringify({ key: keyval.value, label: keylabel.value }),
      });
      keyval.value = ""; // the key never re-renders anywhere — write-only from here on
      keylabel.value = "";
      keyflash.hidden = false;
      void loadKeys();
    } catch (ex) {
      keyerr.textContent = (ex as Error).message;
      keyerr.hidden = false;
    }
  });
}
